import React, { useEffect, useMemo, useState } from 'react'
import {
  ArrowPathIcon,
  ClipboardDocumentListIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  FunnelIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import {
  auditoriaAdminService,
  CatalogoAuditoriaAdmin,
  EventoAuditoriaAdmin,
} from '../../../services/auditoriaAdminService'
import { resolverPresentacionErrorApi } from '../../../services/apiError'
import { useToast } from '../../../ganchos/interfaz-usuario/usarNotificaciones'
import Modal from '../../../componentes/ui/Modal'

const LIMITE_PAGINA = 20
const CATALOGO_VACIO: CatalogoAuditoriaAdmin = { acciones: [], entidades: [] }
const FILTROS_RAPIDOS = [
  { id: 'resets', label: 'Solo resets', cambios: { accion: 'admin.reset_password' } },
  { id: 'altas', label: 'Altas', cambios: { accion: 'create_' } },
  { id: 'actualizaciones', label: 'Actualizaciones', cambios: { accion: 'update_' } },
  { id: 'eliminaciones', label: 'Eliminaciones', cambios: { accion: 'delete_' } },
  { id: 'empresas', label: 'Empresas', cambios: { entidad_tipo: 'organizacion' } },
] as const

const PaginaAuditoriaAdmin: React.FC = () => {
  const { showToast } = useToast()
  const [eventos, setEventos] = useState<EventoAuditoriaAdmin[]>([])
  const [total, setTotal] = useState(0)
  const [cargando, setCargando] = useState(true)
  const [exportando, setExportando] = useState(false)
  const [cargandoCatalogo, setCargandoCatalogo] = useState(true)
  const [paginaActual, setPaginaActual] = useState(1)
  const [catalogo, setCatalogo] = useState<CatalogoAuditoriaAdmin>(CATALOGO_VACIO)
  const [eventoSeleccionado, setEventoSeleccionado] = useState<EventoAuditoriaAdmin | null>(null)
  const [filtroRapidoActivo, setFiltroRapidoActivo] = useState<string | null>(null)
  const [filtros, setFiltros] = useState({
    accion: '',
    actor: '',
    entidad_tipo: '',
    entidad_id: '',
    ip_origen: '',
    fecha_desde: '',
    fecha_hasta: '',
  })

  const totalPaginas = Math.max(1, Math.ceil(total / LIMITE_PAGINA))

  const cargarEventos = async (pagina = paginaActual) => {
    setCargando(true)
    try {
      const respuesta = await auditoriaAdminService.obtenerEventos({
        ...filtros,
        limite: LIMITE_PAGINA,
        offset: (pagina - 1) * LIMITE_PAGINA,
      })
      setEventos(respuesta.items || [])
      setTotal(respuesta.total || 0)
    } catch (error) {
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error al cargar auditoría',
        mensajeFallback: 'No se pudo obtener la bitácora de auditoría administrativa.',
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
      setEventos([])
      setTotal(0)
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => {
    const cargarPantalla = async () => {
      setCargandoCatalogo(true)
      try {
        const [catalogoRespuesta] = await Promise.all([
          auditoriaAdminService.obtenerCatalogo(),
          cargarEventos(1),
        ])
        setCatalogo(catalogoRespuesta)
        setPaginaActual(1)
      } catch (error) {
        const presentacion = resolverPresentacionErrorApi(error, {
          tituloFallback: 'Error al cargar auditoría',
          mensajeFallback: 'No se pudo inicializar la bitácora administrativa.',
        })
        showToast({
          type: 'error',
          title: presentacion.title,
          message: presentacion.message,
        })
      } finally {
        setCargandoCatalogo(false)
      }
    }

    cargarPantalla()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const actualizarFiltros = (parcial: Partial<typeof filtros>) => {
    setFiltroRapidoActivo(null)
    setFiltros((prev) => ({ ...prev, ...parcial }))
  }

  const aplicarFiltros = async () => {
    setPaginaActual(1)
    await cargarEventos(1)
  }

  const aplicarFiltroRapido = async (
    filtroId: string,
    cambios: Partial<typeof filtros>,
  ) => {
    const siguientesFiltros = {
      ...filtros,
      accion: '',
      entidad_tipo: '',
      ...cambios,
    }
    setFiltroRapidoActivo(filtroId)
    setFiltros(siguientesFiltros)
    setPaginaActual(1)
    setCargando(true)
    try {
      const respuesta = await auditoriaAdminService.obtenerEventos({
        ...siguientesFiltros,
        limite: LIMITE_PAGINA,
        offset: 0,
      })
      setEventos(respuesta.items || [])
      setTotal(respuesta.total || 0)
    } catch (error) {
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error al aplicar filtro rápido',
        mensajeFallback: 'No se pudo aplicar el filtro rápido de auditoría.',
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    } finally {
      setCargando(false)
    }
  }

  const limpiarFiltros = async () => {
    setFiltroRapidoActivo(null)
    setFiltros({
      accion: '',
      actor: '',
      entidad_tipo: '',
      entidad_id: '',
      ip_origen: '',
      fecha_desde: '',
      fecha_hasta: '',
    })
    setPaginaActual(1)
    setCargando(true)
    try {
      const respuesta = await auditoriaAdminService.obtenerEventos({
        limite: LIMITE_PAGINA,
        offset: 0,
      })
      setEventos(respuesta.items || [])
      setTotal(respuesta.total || 0)
    } catch (error) {
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error al limpiar filtros',
        mensajeFallback: 'No se pudo recargar la bitácora.',
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    } finally {
      setCargando(false)
    }
  }

  const irAPagina = async (pagina: number) => {
    const paginaNormalizada = Math.min(Math.max(1, pagina), totalPaginas)
    setPaginaActual(paginaNormalizada)
    await cargarEventos(paginaNormalizada)
  }

  const metricas = useMemo(() => {
    const resets = eventos.filter((evento) => evento.accion === 'admin.reset_password').length
    const ipsUnicas = new Set(eventos.map((evento) => evento.ip_origen).filter(Boolean)).size
    const actoresUnicos = new Set(eventos.map((evento) => evento.actor_email).filter(Boolean)).size
    return { resets, ipsUnicas, actoresUnicos }
  }, [eventos])

  const accionesMap = useMemo(
    () => Object.fromEntries(catalogo.acciones.map((accion) => [accion.valor, accion.label])),
    [catalogo.acciones],
  )

  const entidadesMap = useMemo(
    () => Object.fromEntries(catalogo.entidades.map((entidad) => [entidad.valor, entidad.label])),
    [catalogo.entidades],
  )

  const formatearFecha = (fecha?: string | null) => {
    if (!fecha) return 'Sin fecha'
    return new Date(fecha).toLocaleString('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  const extraerObjetivo = (evento: EventoAuditoriaAdmin) => {
    const objetivo = evento.metadata?.usuario_objetivo
    if (objetivo?.correo) {
      return `${objetivo.correo}${objetivo.rol ? ` (${objetivo.rol})` : ''}`
    }
    return evento.entidad_id || 'N/D'
  }

  const formatearAccion = (accion: string) => accionesMap[accion] || accion

  const formatearEntidad = (entidad: string) => entidadesMap[entidad] || entidad

  const exportarCsv = async () => {
    setExportando(true)
    try {
      const blob = await auditoriaAdminService.exportarEventosCsv(filtros)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `auditoria_admin_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      showToast({
        type: 'success',
        title: 'CSV exportado',
        message: 'La bitácora de auditoría se descargó correctamente.',
      })
    } catch (error) {
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error al exportar auditoría',
        mensajeFallback: 'No se pudo descargar el archivo CSV de auditoría.',
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    } finally {
      setExportando(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Auditoría Administrativa</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Consulta eventos sensibles del panel admin como resets de contraseña y acciones críticas.
              </p>
            </div>
            <button
              onClick={() => cargarEventos(paginaActual)}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Recargar
            </button>
            <button
              onClick={exportarCsv}
              disabled={exportando}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {exportando ? 'Exportando...' : 'Exportar CSV'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Eventos</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{total}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <KeyIcon className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Resets en página</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{metricas.resets}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <ShieldCheckIcon className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Actores únicos</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{metricas.actoresUnicos}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <MagnifyingGlassIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">IPs únicas</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{metricas.ipsUnicas}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-2 mb-4">
            <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros</h2>
          </div>
          <div className="mb-4 flex flex-wrap gap-2">
            {FILTROS_RAPIDOS.map((filtro) => (
              <button
                key={filtro.id}
                onClick={() => aplicarFiltroRapido(filtro.id, filtro.cambios)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  filtroRapidoActivo === filtro.id
                    ? 'bg-blue-600 text-white'
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {filtro.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-4">
            <div>
              <label htmlFor="auditoria-filtro-accion" className="sr-only">Filtrar por acción</label>
            <select
              id="auditoria-filtro-accion"
              name="accion"
              value={filtros.accion}
              onChange={(e) => actualizarFiltros({ accion: e.target.value })}
              className="rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              disabled={cargandoCatalogo}
            >
              <option value="">Todas las acciones</option>
              {catalogo.acciones.map((accion) => (
                <option key={accion.valor} value={accion.valor}>
                  {accion.label}
                </option>
              ))}
            </select>
            </div>
            <div>
            <label htmlFor="auditoria-filtro-actor" className="sr-only">Filtrar por actor</label>
            <input
              id="auditoria-filtro-actor"
              name="actor"
              value={filtros.actor}
              onChange={(e) => actualizarFiltros({ actor: e.target.value })}
              placeholder="Actor"
              className="rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            </div>
            <div>
            <label htmlFor="auditoria-filtro-entidad" className="sr-only">Filtrar por entidad</label>
            <select
              id="auditoria-filtro-entidad"
              name="entidad_tipo"
              value={filtros.entidad_tipo}
              onChange={(e) => actualizarFiltros({ entidad_tipo: e.target.value })}
              className="rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              disabled={cargandoCatalogo}
            >
              <option value="">Todas las entidades</option>
              {catalogo.entidades.map((entidad) => (
                <option key={entidad.valor} value={entidad.valor}>
                  {entidad.label}
                </option>
              ))}
            </select>
            </div>
            <div>
            <label htmlFor="auditoria-filtro-entidad-id" className="sr-only">Filtrar por ID objetivo</label>
            <input
              id="auditoria-filtro-entidad-id"
              name="entidad_id"
              value={filtros.entidad_id}
              onChange={(e) => actualizarFiltros({ entidad_id: e.target.value })}
              placeholder="ID objetivo"
              className="rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            </div>
            <div>
            <label htmlFor="auditoria-filtro-ip" className="sr-only">Filtrar por IP origen</label>
            <input
              id="auditoria-filtro-ip"
              name="ip_origen"
              value={filtros.ip_origen}
              onChange={(e) => actualizarFiltros({ ip_origen: e.target.value })}
              placeholder="IP origen"
              className="rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            </div>
            <div>
            <label htmlFor="auditoria-filtro-fecha-desde" className="sr-only">Filtrar desde fecha</label>
            <input
              id="auditoria-filtro-fecha-desde"
              name="fecha_desde"
              type="date"
              value={filtros.fecha_desde}
              onChange={(e) => actualizarFiltros({ fecha_desde: e.target.value })}
              className="rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            </div>
            <div>
            <label htmlFor="auditoria-filtro-fecha-hasta" className="sr-only">Filtrar hasta fecha</label>
            <input
              id="auditoria-filtro-fecha-hasta"
              name="fecha_hasta"
              type="date"
              value={filtros.fecha_hasta}
              onChange={(e) => actualizarFiltros({ fecha_hasta: e.target.value })}
              className="rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button
              onClick={aplicarFiltros}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Aplicar filtros
            </button>
            <button
              onClick={limpiarFiltros}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Limpiar
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Eventos registrados</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Acción</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Actor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Objetivo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">IP</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Descripción</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Detalle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {eventos.map((evento) => (
                  <tr key={evento.id_evento}>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatearFecha(evento.fecha_evento)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        {formatearAccion(evento.accion)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <div>{evento.actor_email || 'Sistema'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{evento.actor_rol || 'N/D'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <div>{formatearEntidad(evento.entidad_tipo)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{extraerObjetivo(evento)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{evento.ip_origen || 'N/D'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{evento.descripcion || 'Sin descripción'}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setEventoSeleccionado(evento)}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <EyeIcon className="h-4 w-4" />
                        Ver detalle
                      </button>
                    </td>
                  </tr>
                ))}
                {!cargando && eventos.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                      No hay eventos que coincidan con los filtros seleccionados.
                    </td>
                  </tr>
                )}
                {cargando && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                      Cargando bitácora...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Mostrando {(paginaActual - 1) * LIMITE_PAGINA + (eventos.length ? 1 : 0)} - {(paginaActual - 1) * LIMITE_PAGINA + eventos.length} de {total}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => irAPagina(paginaActual - 1)}
                disabled={paginaActual === 1 || cargando}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
              >
                Anterior
              </button>
              <span className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400">
                Página {paginaActual} de {totalPaginas}
              </span>
              <button
                onClick={() => irAPagina(paginaActual + 1)}
                disabled={paginaActual >= totalPaginas || cargando}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={Boolean(eventoSeleccionado)}
        onClose={() => setEventoSeleccionado(null)}
        title="Detalle de evento de auditoría"
        size="xl"
      >
        {eventoSeleccionado && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Acción</p>
                <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                  {formatearAccion(eventoSeleccionado.accion)}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{eventoSeleccionado.accion}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Entidad</p>
                <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                  {formatearEntidad(eventoSeleccionado.entidad_tipo)}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  ID: {eventoSeleccionado.entidad_id || 'N/D'}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Actor</p>
                <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                  {eventoSeleccionado.actor_email || 'Sistema'}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Rol: {eventoSeleccionado.actor_rol || 'N/D'}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Contexto</p>
                <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                  {formatearFecha(eventoSeleccionado.fecha_evento)}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  IP: {eventoSeleccionado.ip_origen || 'N/D'}
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Descripción</p>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                {eventoSeleccionado.descripcion || 'Sin descripción'}
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Metadata</p>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Datos completos del evento para soporte y trazabilidad.
                  </p>
                </div>
              </div>
              <pre className="mt-4 max-h-[28rem] overflow-auto rounded-lg bg-gray-950 p-4 text-xs text-green-200">
                {JSON.stringify(eventoSeleccionado.metadata || {}, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default PaginaAuditoriaAdmin
